// src/components/JoinEvent.js
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { standardUserApi } from "../services/standardUserApi";
import useNoIndex from "../hooks/useNoIndex";
import LoadingState from "./LoadingState";

/**
 * Public, no-account guest RSVP page for a self-run event's share link
 * (see SelfRunEvent.share_token in models.py). This is the one surface in
 * the connections/self-run-events feature reachable without an OkGather
 * account - deliberately never the same thing as an official gathering,
 * so RSVPing here never grants access to those.
 */
const JoinEvent = () => {
  const { token } = useParams();
  useNoIndex();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [rsvped, setRsvped] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const memberToken = localStorage.getItem("memberstackToken");

  useEffect(() => {
    const load = async () => {
      try {
        const data = await standardUserApi.getJoinEvent(token);
        setEvent(data);
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleRsvp = async (e) => {
    e.preventDefault();
    if (!memberToken && (!guestName.trim() || !guestEmail.trim())) {
      setErrorMessage("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    setErrorMessage("");
    try {
      await standardUserApi.rsvpToJoinEvent(
        token,
        { guestName: guestName.trim(), guestEmail: guestEmail.trim() },
        memberToken
      );
      setRsvped(true);
    } catch (err) {
      console.error("JoinEvent › rsvp error:", err);
      setErrorMessage("Couldn't submit your RSVP right now. Please try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  if (notFound || !event) {
    return (
      <div style={{ textAlign: "center", padding: "80px 20px" }}>
        <h4 style={{ color: "#111827" }}>This invite link isn't valid</h4>
        <p style={{ color: "#6B7280" }}>It may have been made private, or the link is incorrect.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "560px", margin: "0 auto", padding: "48px 20px" }}>
      <p style={{ textAlign: "center", color: "#8B5FBF", fontWeight: "700", fontSize: "0.85rem", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: "8px" }}>
        You're invited
      </p>
      <h4 style={{ color: "#111827", fontSize: "1.8rem", fontWeight: "800", textAlign: "center", marginBottom: "8px" }}>
        {event.title}
      </h4>
      <p style={{ textAlign: "center", color: "#6B7280", marginBottom: "4px" }}>
        {new Date(event.eventDatetime).toLocaleString()}
      </p>
      {event.location && (
        <p style={{ textAlign: "center", color: "#6B7280", marginBottom: "4px" }}>{event.location}</p>
      )}
      <p style={{ textAlign: "center", color: "#9CA3AF", fontSize: "0.9rem", marginBottom: "24px" }}>
        Hosted by {event.hostFirstName} · {event.goingCount} going
      </p>

      {event.description && (
        <p style={{ color: "#374151", textAlign: "center", marginBottom: "28px" }}>{event.description}</p>
      )}

      {rsvped ? (
        <div style={{ textAlign: "center" }}>
          <p style={{ color: "#15803D", fontWeight: "700", marginBottom: "16px" }}>You're in! See you there. 🎉</p>
          <p style={{ color: "#6B7280", fontSize: "0.9rem", marginBottom: "16px" }}>
            This was a self-run hangout, separate from OkGather's own curated gatherings.
            Curious what those are like?
          </p>
          <Link
            to="/"
            style={{
              display: "inline-block", backgroundColor: "#8B5FBF", color: "#fff", borderRadius: "12px",
              padding: "12px 28px", fontWeight: "700", textDecoration: "none",
            }}
          >
            See OkGather's official events
          </Link>
        </div>
      ) : (
        <form onSubmit={handleRsvp} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {!memberToken && (
            <>
              <input
                type="text"
                placeholder="Your name"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                style={{ padding: "12px 16px", borderRadius: "10px", border: "1.5px solid #E5E7EB" }}
              />
              <input
                type="email"
                placeholder="Your email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                style={{ padding: "12px 16px", borderRadius: "10px", border: "1.5px solid #E5E7EB" }}
              />
            </>
          )}
          {errorMessage && <p style={{ color: "#B91C1C", textAlign: "center" }}>{errorMessage}</p>}
          <button
            type="submit"
            disabled={submitting}
            style={{
              backgroundColor: "#8B5FBF", color: "#fff", border: "none", borderRadius: "12px",
              padding: "14px 28px", fontWeight: "700", cursor: submitting ? "default" : "pointer",
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? "Submitting..." : "I'm going"}
          </button>
          <p style={{ textAlign: "center", color: "#9CA3AF", fontSize: "0.8rem" }}>
            No OkGather account needed to RSVP to this hangout.
          </p>
        </form>
      )}
    </div>
  );
};

export default JoinEvent;
